import React, {
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FormCheckbox } from 'components/Form';
import classNames from 'classnames';
import { AppDataContext } from 'context/appData';
import { ConfigurationDataContext } from 'context/configuration';
import { checkIsFieldError } from 'domain/glass';
import { formatNumberToFixed } from 'utils';
import GlassConfigTableInput from '../GlassConfigTableInput';

const GlassConfigTableRowCell = forwardRef((props, ref) => {
	const {
		freezerRef,
		activeInputRowCellRef,
		glassTypeId,
		cellRefs,
		cellData,
		cellIndex,
		formElementIdRef,
		isPreview = false,
		rowId,
		configurationData,
	} = props;
	const { t } = useTranslation('glassCharacteristics');
	const { errorFields, oversizedGlassFields, showErrors } = useContext(ConfigurationDataContext);
	const { configuratorData } = useContext(AppDataContext);

	const { name, value } = cellData;
	const [isError, setIsError] = useState(false);
	const [isOversized, setIsOversized] = useState(false);
	const [isInputOpened, setInputOpened] = useState(
		(activeInputRowCellRef?.current &&
			activeInputRowCellRef.current.glassTypeId === glassTypeId &&
			activeInputRowCellRef.current.rowId === rowId &&
			activeInputRowCellRef.current.cellIndex === cellIndex) ||
			false
	);
	const [inputValueState, setInputValueState] = useState(value);
	const inputRef = useRef(null);

	const glassConfigTableCellClasses = classNames('glass_config_table__cell');

	const glassTypeIndex = configurationData.glassTypes.findIndex(o => o.id === glassTypeId);
	const glassData = configurationData.glassTypes[glassTypeIndex];
	const rowIndex = glassData.positions.findIndex(r => r.id === rowId);

	const hideInput = () => {
		setInputOpened(false);
		inputRef.current.blur();
	};

	const showInput = () => setInputOpened(true);

	useImperativeHandle(ref, () => ({
		showInput,
	}));

	const setInputValue = useCallback(
		e => {
			const { value: inputValue } = e.target;

			if (cellData.value !== inputValue) {
				freezerRef.current.emit('setCellValue', inputValue, { glassTypeId, rowId, cellIndex });
			}

			hideInput();
		},
		[cellData.value, cellIndex, freezerRef, glassTypeId, rowId]
	);

	const handleInputKeyDown = useCallback(
		e => {
			if (e.which === 13 /* enter */ || e.which === 9 /* tab */) {
				e.preventDefault();
				setInputValue(e);
				if (e.shiftKey) {
					if (cellIndex > 0) {
						cellRefs.current[cellIndex - 1].current.showInput();
					} else if (rowIndex > 0) {
						const prevRowId = glassData.positions[rowIndex - 1].id;
						freezerRef.current.emit('setActiveInputCell', {
							glassTypeId,
							rowId: prevRowId,
							cellIndex: 2,
						});
					}
				} else {
					if (cellIndex >= 2) {
						// next line
						if (rowIndex + 1 < glassData.positions.length) {
							const nextRowId = glassData.positions[rowIndex + 1].id;
							freezerRef.current.emit('setActiveInputCell', {
								glassTypeId,
								rowId: nextRowId,
								cellIndex: 0,
							});
						} else {
							const currentRow = glassData.positions[rowIndex];
							if (currentRow.row[0].value || currentRow.row[1].value || e.target.value) {
								freezerRef.current.emit('addNewPosition', { glassTypeId, setActiveCell: true });
							}
						}
					} else {
						// not handling overflow since the last cell is always a GlassConfigTableRowNotesCell
						cellRefs.current[cellIndex + 1].current.showInput();
					}
				}
			}
		},
		[cellIndex, cellRefs, freezerRef, glassData.positions, glassTypeId, rowIndex, setInputValue]
	);

	const handleCheckboxKeyDown = e => {
		if (e.which === 9 /* tab */) {
			e.preventDefault();
			// not handling cellIndex > 0 since checkboxes are never in first place
			cellRefs.current[cellIndex + (e.shiftKey ? -1 : 1)].current.showInput();
		}
	};

	const onInputChangeHandler = useCallback(
		e => {
			const { value: inputValue } = e.target;

			let num = inputValue.replace(/\D/g, '');
			if (name === 'cantonalInsurance') {
				num = num.slice(0, 6);
			}
			setInputValueState(num);
		},
		[name]
	);

	const onBlurHandler = e => {
		setInputValue(e);
		freezerRef.current.emit('onBlur', { glassTypeId, rowId, cellIndex });

		let ev = e?.target?.value.length > 0;
		if (isError && ev) {
			setIsError(false);
		}
	};

	const onChangeCheckboxHandler = e => {
		const { checked } = e.target;
		const preparedValue = checked ? 'checked' : 'unchecked';

		if (preparedValue !== value) {
			freezerRef.current.emit('setCellValue', preparedValue, { glassTypeId, rowId, cellIndex });
		}
	};

	const onFocusHandler = () => {
		showInput();
	};

	useEffect(() => {
		if (!freezerRef) return null;

		// effect 1
		if (isInputOpened && inputRef.current) {
			inputRef.current.select();
			freezerRef.current.emit('onInput', { glassTypeId, rowId, cellIndex });
		}

		// effect 2 (merged both since they have the same deps)
		const off = freezerRef.current.on(
			'setActiveInputCell',
			({ glassTypeId: gtId, rowId: rId, cellIndex: i }) => {
				if (!isInputOpened && glassTypeId === gtId && rowId === rId && cellIndex === i) {
					setInputOpened(true);
				}
			}
		);
		return off;
	}, [cellIndex, freezerRef, glassTypeId, rowId, isInputOpened]);

	useEffect(() => {
		if (!rowId || !name) return;
		if (errorFields.length > 0) {
			setIsError(checkIsFieldError(errorFields, rowId, name) && showErrors);
		}
		if (oversizedGlassFields.length > 0 && (name === 'length' || name === 'width')) {
			const checkIsFieldOversized = (fields, id, fname) => {
				return fields.some(item => item.id === id && item.row.some(field => field.name === fname));
			};
			setIsOversized(checkIsFieldOversized(oversizedGlassFields, rowId, name));
		} else if (isOversized) {
			setIsOversized(false);
		}
	}, [errorFields, isOversized, oversizedGlassFields, rowId, name, showErrors, setIsOversized]);

	const isInvalid = isError || isOversized;
	const isFirstEmpty = !isPreview && !isInvalid && cellIndex === 0 && rowIndex === 0 && !value;
	const { flatGlassStructure } = glassData;

	return (
		<div className={glassConfigTableCellClasses}>
			{name === 'form' ? (
				<FormCheckbox
					ref={inputRef}
					id={formElementIdRef.current}
					onChange={onChangeCheckboxHandler}
					onKeyDown={handleCheckboxKeyDown}
					defaultChecked={value === 'checked'}
					noLabel
				/>
			) : (
				<div className="glass_config_table__cell_value">
					{isInputOpened ? (
						<GlassConfigTableInput
							ref={inputRef}
							id={formElementIdRef.current}
							name={formElementIdRef.current}
							value={inputValueState}
							onBlur={onBlurHandler}
							onChange={onInputChangeHandler}
							onKeyDown={handleInputKeyDown}
							type="number"
							min="1"
						/>
					) : (
						<button
							className={classNames('glass_config_table__cell_value_in', {
								'glass_config_table__cell_value_in--empty_state': isFirstEmpty,
								'glass_config_table__cell_value_in--invalid_state': isInvalid,
								'glass_config_table__cell_value_in--oversized': isOversized,
							})}
							data-warning-message={
								isOversized &&
								t('glassMaxSizeWarningFormat')
									.replace(
										'{max_width_mm}',
										flatGlassStructure.maxWidth || configuratorData.glassdb.max_glass_width
									)
									.replace(
										'{max_height_mm}',
										flatGlassStructure.maxHeight || configuratorData.glassdb.max_glass_height
									)
							}
							onClick={showInput}
							onFocus={onFocusHandler}
							type="button"
						>
							{value === ''
								? '-'
								: name === 'cantonalInsurance'
									? value
									: formatNumberToFixed(value, 0, t('i18n:numberFormat'))}
						</button>
					)}
				</div>
			)}
		</div>
	);
});

export default GlassConfigTableRowCell;
